import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Permission } from '../types';
import * as authApi from '../api/auth';
import * as usersApi from '../api/users';
import { clearToken, getToken, setToken } from '../auth/token';

interface AuthContextType {
  user: { uid: string; email: string; displayName?: string; photoURL?: string } | null;
  userData: (usersApi.MeResponse & {
    uid?: string;
    displayName?: string;
    notificationPreferences?: Record<string, boolean>;
    permissions?: Permission[];
  }) | null;
  loading: boolean;
  isAdmin: boolean;
  isIntegrator: boolean;
  hasPermission: (permission: Permission) => boolean;
  token: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: {
    firstName: string;
    email: string;
    password: string;
    acceptTerms: true;
    phone?: string;
    companyName?: string;
  }) => Promise<void>;
  signOut: () => void;
  refreshMe: () => Promise<void>;
}

const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  admin: [
    'view_users', 'edit_users', 'manage_roles', 'manage_trials', 
    'manage_integrators', 'configure_notifications', 'send_notifications', 'manage_billing'
  ],
  integrator: [
    'view_users', 'edit_users', 'send_notifications'
  ],
  user: []
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<{ uid: string; email: string; displayName?: string; photoURL?: string } | null>(null);
  const [userData, setUserData] = useState<AuthContextType['userData']>(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const t = token || getToken();
    if (!t) {
      setUser(null);
      setUserData(null);
      return;
    }
    const me = await usersApi.getMe(t);
    setUserData({ ...me, uid: me.publicId, displayName: me.firstName });
    setUser({ uid: me.publicId, email: me.email, displayName: me.firstName });
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!token) {
          setLoading(false);
          return;
        }
        const me = await usersApi.getMe(token);
        if (cancelled) return;
        setUserData({ ...me, uid: me.publicId, displayName: me.firstName });
        setUser({ uid: me.publicId, email: me.email, displayName: me.firstName });
      } catch {
        if (cancelled) return;
        clearToken();
        setTokenState(null);
        setUser(null);
        setUserData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isAdmin = (userData?.role || '') === 'admin';
  const isIntegrator = (userData?.role || '') === 'integrator';

  const hasPermission = (permission: Permission): boolean => {
    if (!userData) return false;
    const defaultPermissions = ROLE_PERMISSIONS[userData.role || 'user'] || [];
    return defaultPermissions.includes(permission);
  };

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await authApi.signIn({ email, password });
    setToken(res.token);
    setTokenState(res.token);
    await refreshMe();
  }, [refreshMe]);

  const signUp = useCallback(async (data: {
    firstName: string;
    email: string;
    password: string;
    acceptTerms: true;
    phone?: string;
    companyName?: string;
  }) => {
    await authApi.signUp(data);
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    setTokenState(null);
    setUser(null);
    setUserData(null);
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    userData,
    loading,
    isAdmin,
    isIntegrator,
    hasPermission,
    token,
    signIn,
    signUp,
    signOut,
    refreshMe,
  }), [user, userData, loading, isAdmin, isIntegrator, token, signIn, signUp, signOut, refreshMe]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
